/**
 * 抽出開始ボタンコンポーネント
 * トピック抽出を開始するためのフローティングボタン
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useMapState, useMapExtraction, useMapUI } from '@/contexts/map';
import { Loader2Icon, PlayIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Progress } from '@/components/ui/progress';

interface ExtractButtonProps {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'center';
  className?: string;
}

const ExtractButton: React.FC<ExtractButtonProps> = ({
  position = 'center',
  className = ''
}) => {
  const { mapData, updateNode, updateEdge } = useMapState();
  const { transcript, isProcessing, setIsProcessing } = useMapUI();
  const { extracting, progress, processAll } = useMapExtraction();
  
  // ボタンが無効化される条件
  const isDisabled = !mapData || !transcript || transcript.length < 20 || isProcessing || extracting;
  
  // ポジションスタイル
  const positionStyles: Record<string, string> = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-20 right-4',
    'bottom-left': 'bottom-20 left-4',
    'center': 'bottom-20 left-1/2 transform -translate-x-1/2'
  };
  
  // 処理開始ハンドラー
  const handleProcess = async () => {
    if (!mapData || !transcript) return;
    
    setIsProcessing(true);
    
    try {
      toast.info('トピック抽出を開始します', {
        description: 'しばらくお待ちください...',
        duration: 3000,
      });
      
      // すべてのノードとエッジを処理
      const result = await processAll(mapData.nodes, mapData.edges, transcript);
      
      // ノードとエッジの更新
      result.nodes.forEach(node => {
        updateNode(node.id, node);
      });
      
      result.edges.forEach(edge => {
        updateEdge(edge.id, edge);
      });
      
      // 処理完了時のトースト
      toast.success('処理完了', {
        description: 'トピックの抽出が完了しました。',
        duration: 3000,
      });
    } catch (error) {
      console.error('Processing error:', error);
      toast.error('エラー', {
        description: '処理中にエラーが発生しました。',
      });
    } finally {
      setIsProcessing(false);
    }
  };
  
  // 進捗表示を追加
  const showProgress = isProcessing || extracting;
  
  return (
    <div className={`fixed ${positionStyles[position]} z-20 ${className}`}>
      <div className="flex flex-col items-center gap-2">
        <Button
          size="lg"
          onClick={handleProcess}
          disabled={isDisabled}
          className="shadow-lg h-14 px-6 rounded-full font-medium"
        >
          {showProgress ? (
            <>
              <Loader2Icon className="mr-2 h-5 w-5 animate-spin" />
              抽出中...
            </>
          ) : (
            <>
              <PlayIcon className="mr-2 h-5 w-5" />
              トピックを抽出
            </>
          )}
        </Button>
        
        {/* 進捗バー */}
        {showProgress && (
          <div className="w-full max-w-xs bg-background/80 backdrop-blur-sm p-2 rounded-full">
            <div className="flex justify-between items-center text-xs text-muted-foreground mb-1">
              <span>処理中...</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-1.5" />
          </div>
        )}
      </div>
    </div>
  );
};

export default ExtractButton;